import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Loader2, Layers } from "lucide-react";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/preview" data-testid="series-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
        <span className="text-[#E30613]">VL</span>
        <span className="text-white">MAG</span>
      </Link>
      <Link
        to="/preview#stories"
        data-testid="series-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={16} /> All Stories
      </Link>
    </div>
  </header>
);

export default function SeriesHub() {
  const [series, setSeries] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    let active = true;
    axios
      .get(`${API}/series`)
      .then((r) => {
        if (!active) return;
        setSeries(r.data.series || []);
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => { active = false; };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen" data-testid="series-hub">
      <TopBar />

      <section className="px-5 md:px-10 pt-[120px] md:pt-[160px] pb-10 md:pb-16 border-b border-white/10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-5">
            <Layers size={14} /> Multi-Part Features
          </p>
          <h1 className="font-display uppercase leading-[0.88] tracking-tight text-5xl sm:text-6xl md:text-8xl">
            The Series
          </h1>
          <p className="text-neutral-400 leading-relaxed mt-6 text-base md:text-lg max-w-2xl">
            Full runs, in order. Binge every chapter of VLMAG's ongoing conversations with the artists, designers and founders moving the culture.
          </p>
        </motion.div>
      </section>

      {status === "loading" ? (
        <div className="flex items-center justify-center py-32" data-testid="series-loading">
          <Loader2 className="animate-spin text-[#E30613]" size={32} />
        </div>
      ) : series.length === 0 ? (
        <div className="px-5 md:px-10 py-28 text-center" data-testid="series-empty">
          <p className="font-display uppercase text-3xl md:text-5xl tracking-tight text-white">No Series Yet</p>
          <p className="text-neutral-500 mt-4">Multi-part features will land here soon.</p>
          <Link to="/preview#stories" className="inline-block mt-8 border border-[#E30613] px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E30613] transition-colors">
            Browse Stories
          </Link>
        </div>
      ) : (
        <div className="px-5 md:px-10 py-14 md:py-20 space-y-16 md:space-y-24" data-testid="series-list">
          {series.map((g) => (
            <section key={g.series} data-testid={`series-group-${g.series.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 pb-5 border-b border-white/10">
                <div>
                  <span className="bg-[#E30613] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">{g.network}</span>
                  <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight mt-4">{g.series}</h2>
                </div>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500">{g.count} Part{g.count > 1 ? "s" : ""}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {g.parts.map((p, i) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                  >
                    <Link
                      to={`/story/${p.id}`}
                      data-testid={`series-part-link-${p.id}`}
                      className="group border border-white/10 hover:border-[#E30613] transition-colors duration-500 block h-full"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img src={mediaUrl(p.image)} alt={p.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                        <span className="absolute top-0 left-0 bg-[#E30613] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                          Part {p.part || "—"}
                        </span>
                      </div>
                      <div className="p-5 md:p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{p.category}</span>
                          <ArrowUpRight size={16} className="text-neutral-600 group-hover:text-[#E30613] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                        </div>
                        <h3 className="font-display uppercase text-xl md:text-2xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300">
                          {p.title}
                        </h3>
                        {p.excerpt && <p className="text-sm text-neutral-400 mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
}
